import { useState } from 'react';
import { Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Alert, FlexColumn } from '@elementary/common-ui/components';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import TestMessageSection from './TestMessageSection';

type Props = {
  token?: string | null;
  regionApiUrl?: string | null;
  onSuccess?: () => void;
  onReset?: () => void;
};

const OpsgenieTestSection = ({
  token,
  regionApiUrl,
  onSuccess,
  onReset,
}: Props) => {
  const [customMessage, setCustomMessage] = useState<string>('');

  const { openSnackbar } = useSnackbar();

  const defaultMessage =
    'This is a test incident from Elementary Data to verify your Opsgenie integration is working correctly.';

  const testMutation = useMutation(
    async (data: {
      message?: string;
      token?: string | null;
      regionApiUrl?: string | null;
    }) => {
      const response = await api.sendOpsgenieTestIncident(data);
      return response;
    },
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test incident sent successfully to Opsgenie!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message:
              response.message || 'Failed to send test incident to Opsgenie',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test incident to Opsgenie',
          variant: 'error',
        });
      },
    },
  );

  const handleSendTest = () => {
    const message = customMessage.trim() || defaultMessage;
    testMutation.mutate({
      message,
      token,
      regionApiUrl,
    });
  };

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test incident to verify your Opsgenie integration is working
        correctly.
      </Typography>

      <Alert severity="warning">
        <strong>Important:</strong> This will create a real incident in your
        Opsgenie account. You will need to manually resolve or close the
        incident after testing.
      </Alert>

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        messageLabel="Test incident message (optional)"
      />
    </FlexColumn>
  );
};

export default OpsgenieTestSection;
